import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Calendar, Lock, LockOpen, Warning, Check, ClockCountdown, ArrowRight } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const STATUS_CONFIG = {
  abierto: { 
    label: 'Abierto', 
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    icon: LockOpen,
    description: 'Los encargados pueden registrar y editar datos'
  },
  en_cierre: { 
    label: 'En Cierre', 
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: ClockCountdown,
    description: 'Periodo de revisión final antes del cierre'
  },
  cerrado: { 
    label: 'Cerrado', 
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: Lock,
    description: 'No se permiten cambios por usuarios de sucursal'
  },
};

const OperativeMonthPage = () => {
  const { axiosInstance } = useAuth();
  const [loading, setLoading] = useState(true);
  const [operativeMonth, setOperativeMonth] = useState(null);
  const [closedMonths, setClosedMonths] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Modals
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showChangeMonthModal, setShowChangeMonthModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  
  // Form data
  const [closeObservation, setCloseObservation] = useState('');
  const [openNextMonth, setOpenNextMonth] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [reopenYear, setReopenYear] = useState(null);
  const [reopenMonth, setReopenMonth] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [opMonthRes, historyRes] = await Promise.all([
        axiosInstance.get('/api/operative-month'),
        axiosInstance.get('/api/operative-month/history'),
      ]);
      setOperativeMonth(opMonthRes.data);
      setClosedMonths(historyRes.data);
    } catch (error) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChangeStatus = async (newStatus) => {
    setSaving(true);
    try {
      await axiosInstance.put('/api/operative-month', { status: newStatus });
      await loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al cambiar estado');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseMonth = async () => {
    setSaving(true);
    try {
      await axiosInstance.post('/api/operative-month/close', {
        observation: closeObservation,
        open_next_month: openNextMonth,
      });
      setShowCloseModal(false);
      setCloseObservation('');
      await loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al cerrar mes');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeMonth = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/api/operative-month', {
        year: selectedYear,
        month: selectedMonth,
        status: 'abierto',
      });
      setShowChangeMonthModal(false);
      await loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al cambiar mes');
    } finally {
      setSaving(false);
    }
  };

  const handleReopenMonth = async () => {
    setSaving(true);
    try {
      await axiosInstance.post('/api/operative-month/reopen', {
        year: reopenYear,
        month: reopenMonth,
      });
      setShowReopenModal(false);
      await loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al reabrir mes');
    } finally {
      setSaving(false);
    }
  };

  const getNextMonth = () => {
    if (!operativeMonth) return { year: new Date().getFullYear(), month: new Date().getMonth() + 1 };
    const nextMonth = operativeMonth.month === 12 ? 1 : operativeMonth.month + 1;
    const nextYear = operativeMonth.month === 12 ? operativeMonth.year + 1 : operativeMonth.year;
    return { year: nextYear, month: nextMonth };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const StatusIcon = STATUS_CONFIG[operativeMonth?.status]?.icon || Calendar;

  return (
    <div className="space-y-6" data-testid="operative-month-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900" data-testid="operative-month-title">
          Mes Operativo
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Control del periodo operativo para usuarios de sucursal
        </p>
      </div>

      {/* Current Operative Month Card */}
      <div className="border border-zinc-200 bg-white p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-4 ${STATUS_CONFIG[operativeMonth?.status]?.color?.split(' ')[0]} rounded-none`}>
              <StatusIcon size={32} className={STATUS_CONFIG[operativeMonth?.status]?.color?.split(' ')[1]} />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-zinc-900">
                {operativeMonth ? `${MONTHS[operativeMonth.month - 1]} ${operativeMonth.year}` : 'No configurado'}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-3 py-1 text-sm font-medium ${STATUS_CONFIG[operativeMonth?.status]?.color}`}>
                  {STATUS_CONFIG[operativeMonth?.status]?.label}
                </span>
              </div>
              <p className="text-sm text-zinc-500 mt-2">
                {STATUS_CONFIG[operativeMonth?.status]?.description}
              </p>
            </div>
          </div>
        </div>

        {/* Status Controls */}
        <div className="mt-6 pt-6 border-t border-zinc-200">
          <h3 className="text-sm font-medium text-zinc-700 mb-3">Cambiar Estado</h3>
          <div className="flex flex-wrap gap-3">
            {operativeMonth?.status !== 'abierto' && (
              <button
                onClick={() => handleChangeStatus('abierto')}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition-colors"
                data-testid="set-status-abierto"
              >
                <LockOpen size={18} />
                Abrir
              </button>
            )}
            {operativeMonth?.status !== 'en_cierre' && (
              <button
                onClick={() => handleChangeStatus('en_cierre')}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
                data-testid="set-status-en-cierre"
              >
                <ClockCountdown size={18} />
                Iniciar Cierre
              </button>
            )}
            {operativeMonth?.status !== 'cerrado' && (
              <button
                onClick={() => setShowCloseModal(true)}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-800 hover:bg-red-200 transition-colors"
                data-testid="close-month-btn"
              >
                <Lock size={18} />
                Cerrar Mes
              </button>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 pt-6 border-t border-zinc-200">
          <h3 className="text-sm font-medium text-zinc-700 mb-3">Acciones</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => {
                const next = getNextMonth();
                setSelectedYear(next.year);
                setSelectedMonth(next.month);
                setShowChangeMonthModal(true);
              }}
              className="btn-secondary flex items-center gap-2"
              data-testid="advance-month-btn"
            >
              <ArrowRight size={18} />
              Avanzar a Siguiente Mes
            </button>
            <button
              onClick={() => setShowChangeMonthModal(true)}
              className="btn-secondary flex items-center gap-2"
              data-testid="change-month-btn"
            >
              <Calendar size={18} />
              Cambiar Mes Manualmente
            </button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="border border-zinc-200 bg-zinc-50 p-6">
        <h3 className="font-heading font-semibold text-zinc-900 mb-3">¿Cómo funciona el Mes Operativo?</h3>
        <div className="space-y-3 text-sm text-zinc-600">
          <p>
            <strong>Para usuarios de sucursal:</strong> Solo pueden ver y editar el mes operativo vigente. 
            No pueden navegar a otros meses ni ver historial.
          </p>
          <p>
            <strong>Estados del mes:</strong>
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Abierto:</strong> Los encargados pueden registrar y modificar datos normalmente.</li>
            <li><strong>En Cierre:</strong> Periodo de revisión. Los encargados aún pueden editar para completar registros pendientes.</li>
            <li><strong>Cerrado:</strong> El mes queda bloqueado. Solo el administrador puede hacer cambios.</li>
          </ul>
          <p className="mt-3">
            <strong>Ejemplo:</strong> Si estamos en abril pero aún necesita completar marzo, 
            puede mantener marzo como mes operativo hasta terminar la carga.
          </p>
        </div>
      </div>

      {/* Closed Months History */}
      {closedMonths.length > 0 && (
        <div className="border border-zinc-200 bg-white">
          <div className="p-4 border-b border-zinc-200">
            <h3 className="font-heading font-semibold text-zinc-900">Historial de Meses Cerrados</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="closed-months-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Mes
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Fecha de Cierre
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Observación
                  </th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {closedMonths.map((month) => (
                  <tr key={month.id} className="border-b border-zinc-100">
                    <td className="px-4 py-3 text-sm font-medium text-zinc-900">
                      {MONTHS[month.month - 1]} {month.year}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600">
                      {month.close_date ? new Date(month.close_date).toLocaleDateString('es-CL') : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600 max-w-xs truncate">
                      {month.close_observation || '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => {
                          setReopenYear(month.year);
                          setReopenMonth(month.month);
                          setShowReopenModal(true);
                        }}
                        className="text-sm text-zinc-600 hover:text-zinc-900"
                        data-testid={`reopen-month-${month.year}-${month.month}`}
                      >
                        Reabrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Close Month Modal */}
      <Dialog open={showCloseModal} onOpenChange={setShowCloseModal}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Lock size={20} className="text-red-600" />
              Cerrar Mes Operativo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              Está por cerrar <strong>{operativeMonth ? `${MONTHS[operativeMonth.month - 1]} ${operativeMonth.year}` : ''}</strong>.
              Los usuarios de sucursal ya no podrán editar este mes.
            </p>
            <div>
              <label className="form-label">Observación de Cierre (opcional)</label>
              <textarea
                value={closeObservation}
                onChange={(e) => setCloseObservation(e.target.value)}
                rows={2}
                className="form-input resize-none"
                placeholder="Agregar nota de cierre..."
                data-testid="close-observation-input"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="openNextMonth"
                checked={openNextMonth}
                onChange={(e) => setOpenNextMonth(e.target.checked)}
                className="w-4 h-4"
                data-testid="open-next-month-checkbox"
              />
              <label htmlFor="openNextMonth" className="text-sm text-zinc-700">
                Abrir automáticamente {MONTHS[getNextMonth().month - 1]} {getNextMonth().year} como nuevo mes operativo
              </label>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCloseModal(false)}
                className="rounded-none"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCloseMonth}
                disabled={saving}
                className="rounded-none bg-red-600 hover:bg-red-700"
                data-testid="confirm-close-btn"
              >
                {saving ? 'Cerrando...' : 'Cerrar Mes'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Month Modal */}
      <Dialog open={showChangeMonthModal} onOpenChange={setShowChangeMonthModal}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading">Cambiar Mes Operativo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              Seleccione el nuevo mes operativo. Los usuarios de sucursal solo podrán ver y editar este mes.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Año</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="form-select"
                  data-testid="select-year"
                >
                  {[2024, 2025, 2026, 2027].map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Mes</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="form-select"
                  data-testid="select-month"
                >
                  {MONTHS.map((month, index) => (
                    <option key={month} value={index + 1}>{month}</option>
                  ))}
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowChangeMonthModal(false)}
                className="rounded-none"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleChangeMonth}
                disabled={saving}
                className="rounded-none bg-zinc-900"
                data-testid="confirm-change-month-btn"
              >
                {saving ? 'Guardando...' : 'Cambiar Mes'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reopen Month Modal */}
      <Dialog open={showReopenModal} onOpenChange={setShowReopenModal}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Warning size={20} className="text-amber-600" />
              Reabrir Mes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              ¿Está seguro de reabrir <strong>{reopenMonth && reopenYear ? `${MONTHS[reopenMonth - 1]} ${reopenYear}` : ''}</strong>?
              Este mes se convertirá en el nuevo mes operativo activo.
            </p>
            <div className="p-3 bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <strong>Nota:</strong> Esto permitirá a los usuarios de sucursal editar este mes nuevamente.
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowReopenModal(false)}
                className="rounded-none"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleReopenMonth}
                disabled={saving}
                className="rounded-none bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-reopen-btn"
              >
                {saving ? 'Reabriendo...' : 'Reabrir Mes'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperativeMonthPage;
